'client id';

import { PermissionEnum } from 'app/[locale]/w/[workspaceId]/components/role/types';
import {
  BaseTeamRole,
  BaseWorkspaceRole,
  MemberDetailWithTeams,
  MemberWorkspaceRole,
} from 'callabo-api/src';
import { useState } from 'react';
import { z } from 'zod';

export type MemberMeModel = {
  id: number;
  userId: number;
  email: string;
  name: string;
  workspaceRoles: Array<MemberWorkspaceRole>;
  baseRole: BaseWorkspaceRole;
  workspacePermissions: Partial<Record<PermissionEnum, 'allow' | 'deny' | undefined>>;
  teamMembers: Array<{
    id: number;
    team: {
      id: number;
      name: string;
    };
    baseRole: BaseTeamRole;
    teamRoles: Array<{
      id: number;
    }>;
    roleTeamPermissions: Partial<Record<PermissionEnum, 'allow' | 'deny' | undefined>>;
  }>;
};

export function useMembersMe(memberMe: MemberDetailWithTeams) {
  const [me] = useState<MemberMeModel | undefined>(memberDetailToMeModel(memberMe));

  return me;
}

const meModelSchema = z.object({
  workspace_permissions: z.record(
    z.nativeEnum(PermissionEnum),
    z.union([z.literal('allow'), z.undefined()])
  ),
  team_members: z.array(
    z.object({
      id: z.number(),
      team: z.object({
        id: z.number(),
        name: z.string(),
      }),
      base_role: z.nativeEnum(BaseTeamRole),
      team_roles: z.array(z.any()),
      role_team_permissions: z.record(
        z.nativeEnum(PermissionEnum),
        z.union([z.literal('allow'), z.undefined()])
      ),
    })
  ),
});
export function memberDetailToMeModel(me: MemberDetailWithTeams): MemberMeModel {
  const parsed = meModelSchema.safeParse(me);
  if (parsed.success !== true) {
    console.error(parsed.error);
    throw new Error('Invalid member detail');
  }

  return {
    id: me.id,
    userId: me.user_id,
    email: me.email,
    name: me.name,
    workspaceRoles: me.workspace_roles,
    baseRole: me.base_role,
    workspacePermissions: parsed.data.workspace_permissions,
    teamMembers: parsed.data.team_members.map((teamMember) => ({
      id: teamMember.id,
      team: {
        id: teamMember.team.id,
        name: teamMember.team.name,
      },
      baseRole: teamMember.base_role,
      teamRoles: teamMember.team_roles,
      roleTeamPermissions: teamMember.role_team_permissions,
    })),
  };
}
